import { Link } from '@tanstack/react-router';
import { Card } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { extractFirstUrl } from './join-utils';
import { CARD_STYLES } from './tactile-card-styles';

interface LiveNowBannerProps {
  classId: string;
  name: string;
  eventStartTime: string;
  eventLocation?: string | null;
  isLive: boolean;
  completedBlocks: number;
}

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
});

export function LiveNowBanner({
  classId,
  name,
  eventStartTime,
  eventLocation,
  isLive,
  completedBlocks,
}: LiveNowBannerProps) {
  const { t } = useTranslation('admin');
  const joinUrl = extractFirstUrl(eventLocation);
  const label = isLive
    ? t('p2e.classes.page.liveBanner.live')
    : t('p2e.classes.page.liveBanner.soon');
  const joinLabel = t('p2e.classes.page.liveBanner.join');
  const time = timeFormatter.format(new Date(eventStartTime));

  const hasCompletedBlocks = Number.isFinite(completedBlocks) && completedBlocks > 0;
  const lastBlockIdx = hasCompletedBlocks ? Math.max(completedBlocks - 1, 0) : undefined;
  const linkTo = hasCompletedBlocks ? '/classes/$classId/block/$blockIdx' : '/classes/$classId';
  const linkParams = hasCompletedBlocks
    ? { classId, blockIdx: String(lastBlockIdx) }
    : { classId };

  return (
    <Link to={linkTo} params={linkParams} className="outline-none">
      <Card
        className={`group relative flex w-full flex-col gap-3 overflow-hidden rounded-xl p-5 cursor-pointer transform transition-all duration-100 ease-out sm:flex-row sm:items-center sm:justify-between ${CARD_STYLES.dark}`}
      >
        <div className="flex items-center gap-4">
          <span className="relative inline-flex h-3 w-3 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-white" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/85">{label}</p>
            <p className="truncate text-lg font-bold text-white">{name}</p>
            <p className="text-xs text-white/70">{time}</p>
          </div>
        </div>
        {joinUrl && (
          <a
            href={joinUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex shrink-0 items-center justify-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-[#cf2e2e] shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_2px_0_rgba(0,0,0,0.08)] hover:bg-rose-50"
          >
            {joinLabel}
          </a>
        )}
      </Card>
    </Link>
  );
}
